/**
 * Permission utility functions for handling browser permissions
 */

export interface PermissionStatus {
  granted: boolean;
  error?: string;
}

export interface AllPermissions {
  location: PermissionStatus;
  microphone: PermissionStatus;
  camera: PermissionStatus;
}

/**
 * Request location permission
 */
export const requestLocationPermission = async (): Promise<PermissionStatus> => {
  try {
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      return { granted: false, error: 'Geolocation is not supported by this browser' };
    }

    // Request permission by attempting to get current position
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve({ granted: true }),
        (error) => {
          let errorMessage = 'Location access denied';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
          }
          resolve({ granted: false, error: errorMessage });
        },
        { timeout: 10000 }
      );
    });
  } catch (error) {
    return { granted: false, error: 'Failed to request location permission' };
  }
};

/**
 * Request microphone permission
 */
export const requestMicrophonePermission = async (): Promise<PermissionStatus> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Stop the stream immediately as we only needed permission
    stream.getTracks().forEach(track => track.stop());
    return { granted: true };
  } catch (error: any) {
    let errorMessage = 'Microphone access denied';
    if (error.name === 'NotAllowedError') {
      errorMessage = 'Microphone access denied by user';
    } else if (error.name === 'NotFoundError') {
      errorMessage = 'No microphone found';
    } else if (error.name === 'NotSupportedError') {
      errorMessage = 'Microphone not supported';
    }
    return { granted: false, error: errorMessage };
  }
};

/**
 * Request camera permission
 */
export const requestCameraPermission = async (): Promise<PermissionStatus> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    // Stop the stream immediately as we only needed permission
    stream.getTracks().forEach(track => track.stop());
    return { granted: true };
  } catch (error: any) {
    let errorMessage = 'Camera access denied';
    if (error.name === 'NotAllowedError') {
      errorMessage = 'Camera access denied by user';
    } else if (error.name === 'NotFoundError') {
      errorMessage = 'No camera found';
    } else if (error.name === 'NotSupportedError') {
      errorMessage = 'Camera not supported';
    }
    return { granted: false, error: errorMessage };
  }
};

/**
 * Request all permissions at once
 */
export const requestAllPermissions = async (): Promise<AllPermissions> => {
  const [location, microphone, camera] = await Promise.all([
    requestLocationPermission(),
    requestMicrophonePermission(),
    requestCameraPermission()
  ]);

  return { location, microphone, camera };
};

/**
 * Check if all critical permissions are granted
 */
export const arePermissionsGranted = (permissions: AllPermissions): boolean => {
  // At minimum, we need microphone for voice interaction
  // Location and camera are preferred but not strictly required
  return permissions.microphone.granted;
};

/**
 * Get permission status without requesting
 */
export const checkPermissionStatus = async (name: PermissionName): Promise<boolean> => {
  try {
    if ('permissions' in navigator) {
      const result = await navigator.permissions.query({ name });
      return result.state === 'granted';
    }
    return false;
  } catch {
    return false;
  }
};
